import { useState } from 'react';
import confetti from 'canvas-confetti';

// Animated Flames Component for High Streaks - Enhanced CSS Animation
export function AnimatedFlames({ streakDays, className = '' }) {
  if (streakDays < 7) return null;
  
  // More flames for higher streaks
  const flameCount = Math.min(Math.floor(streakDays / 7), 5);
  
  // Intensity increases with streak
  const intensity = Math.min(streakDays / 30, 1);
  
  return (
    <div className={`relative flex items-center justify-center ${className}`}>
      {/* Glow effect behind flames */}
      <div 
        className="absolute w-12 h-8 rounded-full blur-lg animate-pulse"
        style={{
          background: `radial-gradient(circle, rgba(255,100,0,${0.3 + intensity * 0.4}) 0%, transparent 70%)`,
        }}
      />
      
      {/* Individual flame elements */}
      {Array.from({ length: flameCount }).map((_, i) => (
        <div
          key={i}
          className="absolute"
          style={{
            left: `${50 + (i - flameCount/2) * 12}%`,
            transform: 'translateX(-50%)',
          }}
        >
          {/* Main flame body */}
          <div
            className="w-3 h-5 rounded-full"
            style={{
              background: 'linear-gradient(to top, #ff4500, #ff6b00, #ffd700)',
              filter: 'blur(1px)',
              animation: `flameFlicker 0.${3 + i}s ease-in-out infinite alternate`,
              animationDelay: `${i * 100}ms`,
            }}
          />
          {/* Inner bright core */}
          <div
            className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-3 rounded-full bg-yellow-200"
            style={{
              filter: 'blur(0.5px)',
              animation: `flamePulse 0.${4 + i}s ease-in-out infinite`,
              animationDelay: `${i * 50}ms`,
            }}
          />
        </div>
      ))}
      
      {/* CSS Keyframes injected as style */}
      <style>{`
        @keyframes flameFlicker {
          0% { transform: scaleY(1) scaleX(1); opacity: 0.9; }
          50% { transform: scaleY(1.1) scaleX(0.95); opacity: 1; }
          100% { transform: scaleY(0.95) scaleX(1.05); opacity: 0.85; }
        }
        @keyframes flamePulse {
          0% { opacity: 0.7; transform: translateX(-50%) scaleY(1); }
          50% { opacity: 1; transform: translateX(-50%) scaleY(1.15); }
          100% { opacity: 0.8; transform: translateX(-50%) scaleY(0.9); }
        }
      `}</style>
    </div>
  );
}

// Shareable Milestone Card Component
export function ShareableMilestoneCard({ 
  streakDays,
  title,
  totalSaved,
  billsPaid,
  onClose,
  onShare
}) {
  const [copied, setCopied] = useState(false);
  
  const handleShare = async () => {
    const shareText = `🔥 ${streakDays} Day Streak! I'm at "${title}" status on GuapGoals! 💰 Saved $${totalSaved?.toLocaleString() || 0} this month. #GuapGoals #FinancialFreedom #MoneyMoves`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My GuapGoals Achievement! 🎉',
          text: shareText,
          url: window.location.origin,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#CCFF00', '#FFD700', '#FF6B6B', '#4ECDC4'],
    });
    
    onShare?.();
  };

  const handleDownload = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 600;
    canvas.height = 400;
    
    const gradient = ctx.createLinearGradient(0, 0, 600, 400);
    gradient.addColorStop(0, '#1a1a2e');
    gradient.addColorStop(1, '#16213e');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 600, 400);
    
    ctx.shadowColor = '#CCFF00';
    ctx.shadowBlur = 30;
    
    ctx.font = 'bold 48px Arial';
    ctx.fillStyle = '#CCFF00';
    ctx.textAlign = 'center';
    ctx.fillText(`🔥 ${streakDays} DAY STREAK`, 300, 100);
    
    ctx.font = 'bold 32px Arial';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(title || 'Street Cred', 300, 160);
    
    ctx.shadowBlur = 0;
    ctx.font = '24px Arial';
    ctx.fillStyle = '#4ECDC4';
    ctx.fillText(`💰 $${totalSaved?.toLocaleString() || 0} Saved`, 300, 220);
    ctx.fillText(`✅ ${billsPaid || 0} Bills Paid`, 300, 260);
    
    ctx.font = 'bold 20px Arial';
    ctx.fillStyle = '#666';
    ctx.fillText('GuapGoals', 300, 360);
    
    const link = document.createElement('a');
    link.download = `guapgoals-streak-${streakDays}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-md w-full animate-in zoom-in-95 duration-200"
      >
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl p-6 border border-gray-700 shadow-2xl">
          <div className="absolute -top-6 left-1/2 -translate-x-1/2">
            <AnimatedFlames streakDays={streakDays} />
          </div>
          
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white text-xl"
          >
            ✕
          </button>
          
          <div className="text-center pt-4">
            <div className="text-6xl mb-4 animate-bounce">🔥</div>
            
            <h2 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500">
              {streakDays} DAY STREAK
            </h2>
            
            <p className="text-2xl font-bold text-white mt-2">{title}</p>
            
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="bg-white/10 rounded-2xl p-4">
                <p className="text-3xl font-black text-[#CCFF00]">
                  ${totalSaved?.toLocaleString() || 0}
                </p>
                <p className="text-gray-400 text-sm">Saved</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-4">
                <p className="text-3xl font-black text-[#4ECDC4]">
                  {billsPaid || 0}
                </p>
                <p className="text-gray-400 text-sm">Bills Paid</p>
              </div>
            </div>
            
            <p className="text-gray-500 text-sm mt-6">GuapGoals</p>
          </div>
          
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleShare}
              className="flex-1 py-3 bg-gradient-to-r from-[#CCFF00] to-[#9ACD32] text-black font-bold rounded-xl hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            >
              {copied ? '✓ Copied!' : '📤 Share'}
            </button>
            
            <button
              onClick={handleDownload}
              className="flex-1 py-3 bg-white/10 text-white font-bold rounded-xl hover:bg-white/20 transition-colors flex items-center justify-center gap-2"
            >
              📥 Save Image
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hook to trigger milestone share
export function useMilestoneShare() {
  const [showMilestone, setShowMilestone] = useState(false);
  const [milestoneData, setMilestoneData] = useState(null);
  
  const triggerMilestone = (data) => {
    setMilestoneData(data);
    setShowMilestone(true);
  };
  
  const closeMilestone = () => {
    setShowMilestone(false);
    setMilestoneData(null);
  };
  
  return {
    showMilestone,
    milestoneData,
    triggerMilestone,
    closeMilestone,
  };
}

export default ShareableMilestoneCard;
